import { useIsFetching } from '@tanstack/react-query';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LoadingScreen from '../../components/loading-screen';
import { isBackgroundQuery } from '../../utils/backgroundQuery';
import { getRouteTransitionScope } from './routeTransitionScope';

/**
 * Porta de transição entre telas do dashboard.
 *
 * O carregamento do produto pertence à navegação, não ao interior das telas: ao
 * alternar entre itens do menu, a área de conteúdo mostra o `LoadingScreen` — com
 * o menu e o header visíveis ao lado — e a tela de destino só aparece quando está
 * pronta. Fora do resume-shell, nenhuma página precisa indicar carregamento no
 * próprio corpo.
 *
 * O `Suspense` de rota (`routes/elements.js`) já cobria apenas a primeira visita a
 * cada tela, enquanto o chunk era baixado. Com o chunk em cache, a troca de item do
 * menu montava a tela vazia na hora, sem nenhum sinal de carregamento — é essa
 * lacuna que esta porta fecha.
 *
 * "Pronta" quer dizer sem requisição **da tela** em voo. As consultas de fundo
 * (`utils/backgroundQuery.js`: header, menu, contextos do layout, polling) não
 * contam: elas não são o que a tela precisa para aparecer, e esperar por elas
 * fazia toda página do dashboard aparecer só depois do layout.
 *
 * A porta reage à troca de **escopo** (`routeTransitionScope.js`), não de
 * `pathname`. Dentro de `/products/resume-shell/:id` o escopo é a raiz do shell:
 * trocar de item na sidebar não reabre a porta, senão cabeçalho e sidebar do shell
 * sumiriam atrás do loading de tela inteira. Ali a regra acima se inverte — quem
 * indica carregamento é o `Suspense` local do shell e cada tela, com
 * `LoadingScreen contained` ou skeleton no próprio corpo.
 *
 * Por que a tela de destino é montada (e não apenas escondida atrás de um `if`):
 * ela precisa montar para que as suas requisições comecem. Fica com
 * `visibility: hidden`, que mantém o layout calculado — gráficos e tabelas que
 * medem o próprio tamanho continuam funcionando — sem nada aparecer.
 */

/**
 * Tempo sem nenhuma requisição em voo para considerar a tela pronta. Existe porque
 * no instante seguinte à montagem as queries ainda não começaram: sem essa folga, a
 * porta abriria antes de a tela ter o que mostrar.
 */
const SETTLED_GRACE_MS = 150;

/**
 * Teto de espera. A porta nunca fica fechada indefinidamente por causa de uma
 * requisição lenta — passado o teto, a tela aparece do jeito que estiver.
 *
 * Com as consultas de fundo fora da conta, o teto ainda cobre o que a marca não
 * alcança: a requisição lenta da própria tela e a consulta global que ninguém
 * marcou. Deixado curto de propósito — é melhor a tela aparecer buscando do que
 * a navegação parecer travada.
 */
const MAX_WAIT_MS = 2500;

const isScreenQuery = (query) => !isBackgroundQuery(query);

RouteTransitionGate.propTypes = {
  children: PropTypes.node,
};

export default function RouteTransitionGate({ children }) {
  const { pathname } = useLocation();
  const scope = getRouteTransitionScope(pathname);
  const pendingRequests = useIsFetching({ predicate: isScreenQuery });

  const [isTransitioning, setIsTransitioning] = useState(true);
  const [gatedScope, setGatedScope] = useState(scope);

  const settledTimerRef = useRef(null);
  const maxWaitTimerRef = useRef(null);

  const clearTimers = () => {
    if (settledTimerRef.current) clearTimeout(settledTimerRef.current);
    if (maxWaitTimerRef.current) clearTimeout(maxWaitTimerRef.current);
    settledTimerRef.current = null;
    maxWaitTimerRef.current = null;
  };

  /**
   * A transição é ligada **durante a renderização**, não em efeito. Efeito roda
   * depois do commit: a tela de destino chegava a pintar um frame visível — sem
   * loading e sem dados — antes de o loading entrar. Atualizar o estado aqui faz
   * o React descartar esta renderização e refazê-la antes de pintar, então esse
   * frame não existe.
   */
  if (gatedScope !== scope) {
    setGatedScope(scope);
    setIsTransitioning(true);
  }

  useEffect(() => {
    maxWaitTimerRef.current = setTimeout(() => setIsTransitioning(false), MAX_WAIT_MS);

    return clearTimers;
  }, [scope]);

  useEffect(() => {
    if (!isTransitioning) return undefined;

    if (pendingRequests > 0) {
      if (settledTimerRef.current) {
        clearTimeout(settledTimerRef.current);
        settledTimerRef.current = null;
      }

      return undefined;
    }

    settledTimerRef.current = setTimeout(() => setIsTransitioning(false), SETTLED_GRACE_MS);

    return () => {
      if (settledTimerRef.current) clearTimeout(settledTimerRef.current);
      settledTimerRef.current = null;
    };
  }, [isTransitioning, pendingRequests]);

  return (
    <>
      {isTransitioning && <LoadingScreen />}

      <Box
        data-testid="route-transition-content"
        sx={{
          width: 1,
          ...(isTransitioning && { visibility: 'hidden' }),
        }}
      >
        {children}
      </Box>
    </>
  );
}
